import React, { useEffect, useState } from 'react';
import { PartnerStatus } from '@marageti/z4-sdk/lib/travel';
import ZChips from '../../z-chips';
import OryxMultiSelect from '../oryx-shared/oryx-multiselect';
import OryxButton from '../oryx-shared/oryx-button/type-button';
import { useCompaniesStore, useLocationsStore } from '../oryx-state';
import { statusLabelMap } from '../utils/companies';
import styles from './companies.module.scss';


const profileStatusOptions = [
  {
    name: 'Published',
    value: 'true',
    displayValue: 'Published',
    checked: false,
    type: 'profile',
  },
  {
    name: 'Unpublished',
    value: 'false',
    displayValue: 'Unpublished',
    checked: false,
    type: 'profile',
  },
];

const nameOptions = [
  {
    name: 'Included',
    value: 'true',
    displayValue: 'Included',
    checked: false,
    type: 'name',
  },
  {
    name: 'Missing',
    value: 'false',
    displayValue: 'Missing',
    checked: false,
    type: 'name',
  },
];

// Photo / Description / First Name filter values feed applyClientSideCompanyFilters; Z4 does not index those fields.
const photoOptions = [
  {
    name: 'Included',
    value: 'true',
    displayValue: 'Included',
    checked: false,
    type: 'photo',
  },
  {
    name: 'Missing',
    value: 'false',
    displayValue: 'Missing',
    checked: false,
    type: 'photo',
  },
];

const descriptionOptions = [
  {
    name: 'Included',
    value: 'true',
    displayValue: 'Included',
    checked: false,
    type: 'description',
  },
  {
    name: 'Missing',
    value: 'false',
    displayValue: 'Missing',
    checked: false,
    type: 'description',
  },
];

// Display order for the Status filter dropdown — mirrors the partner lifecycle.
const STATUS_FILTER_ORDER = [
  PartnerStatus.ACTIVE,
  PartnerStatus.NON_STRATEGIC,
  PartnerStatus.PHASING_OUT,
  PartnerStatus.NEW,
  PartnerStatus.ONBOARDING,
  PartnerStatus.INACTIVE,
];

const statusOptions = STATUS_FILTER_ORDER.map((status) => ({
  name: statusLabelMap[status],
  value: status,
  displayValue: statusLabelMap[status],
  checked: false,
  type: 'status',
}));

const getSelectedOptions = (arr) => (
  arr.reduce((acc, cur) => {
    if (cur.checked && cur.type !== 'GROUP_LABEL') {
      return [...acc, cur.value];
    }
    return acc;
  }, [])
);

const generateLabel = (type, initialVal) => {
  let res;
  switch (type) {
    case 'published': {
      res = initialVal;
      break;
    }
    case 'firstNameExists': {
      res = `First Name: ${initialVal}`;
      break;
    }
    case 'photoExists': {
      res = `Photo: ${initialVal}`;
      break;
    }
    case 'descriptionExists': {
      res = `Description: ${initialVal}`;
      break;
    }
    case 'primaryLocations': {
      res = `Specialized Country: ${initialVal}`;
      break;
    }
    case 'secondaryLocations': {
      res = `Secondary Country: ${initialVal}`;
      break;
    }
    case 'statuses': {
      res = `Status: ${initialVal}`;
      break;
    }
    default:
      break;
  }
  return res;
};

const mapOptionsToChips = (type, options) => {
  const ret = [];
  for (let i = 0; i < options.length; i++) {
    const cur = options[i];
    if(cur.checked){
      ret.push({
        value: cur.value,
        label: generateLabel(type, cur.name),
        type,
      });
    }
  }
  return ret;
}

const getCheckedOptions = (options, filters) => {
  if (!filters) return options;
  let res = options.map((option) => {
    const cur = {...option};
    cur.checked = filters.includes(cur.value);
    return cur;
  });
  return res;
}

const getLocationOptions = (type, countryNames, filters) => {
  let res = [];
  for (let i = 0; i < countryNames.length; i++) {
    const location = countryNames[i];
    res.push({
      name: location,
      value: location,
      displayValue: location,
      checked: filters.includes(location),
      group: 'all',
      type,
    })
  }
  return res;
}

const mergeCountryNames = (countryNames, ...selectedLists) => {
  const names = new Set(countryNames);
  selectedLists.forEach((selected) => {
    (selected || []).forEach((name) => names.add(name));
  });
  return [...names].sort((a, b) => a.localeCompare(b));
}


// Since the value property of a <input> is stringified if its a boolean
// this maps our boolean filters to an option with value='true'/'false'.
const getOptionsFromBoolFilter = (options, filter) => {
  if (typeof filter !== 'boolean') {
    return options;
  }
  const ret = options.map((option) => {
    const cur = {...option};
    if(cur.value === `${filter}`) {
      cur.checked = true;
    }
    return cur;
  });
  return ret;
}


const CompaniesFiltersView = ({
  onReset,
  onFilterChange,
}) => {
  const [profileStatus, setProfileStatusOptions] = useState(profileStatusOptions);
  const [firstName, setNameOptions] = useState(nameOptions);
  const [photo, setPhotoOptions] = useState(photoOptions);
  const [description, setonDescriptionOptions] = useState(descriptionOptions);
  const [statusFilter, setStatusFilterOptions] = useState(statusOptions);
  const [primaryLocation, setPrimaryLocationOptions] = useState([]);
  const [secondaryLocation, setSecondaryLocationOptions] = useState([]);
  const [activeChips, setActiveChips] = useState([]);
  const [countryNames, setCountryNames] = useState([]);

  const { getCountryCodeToNameMap } = useLocationsStore();

  const {
    filters,
    setProfileStatus,
    setFirstNameExists,
    setDescriptionExists,
    setPhotoExists,
    setPrimaryLocations,
    setSecondaryLocations,
    setStatuses,
  } = useCompaniesStore();

  useEffect(() => {
    if (!getCountryCodeToNameMap) return;
    getCountryCodeToNameMap()
      .then((map) => {
        setCountryNames(Object.values(map).sort((a, b) => a.localeCompare(b)));
      })
      .catch(() => {});
  }, [getCountryCodeToNameMap]);

  useEffect(() => {
    const {
      published,
      firstNameExists,
      photoExists,
      descriptionExists,
      primaryLocations,
      secondaryLocations,
      statuses,
    } = filters;
    const profileOptions = getOptionsFromBoolFilter(profileStatusOptions, published);
    setProfileStatusOptions(profileOptions)
    const profileChips = mapOptionsToChips('published', profileOptions);
    const firstNameOptions = getOptionsFromBoolFilter(nameOptions ,firstNameExists)
    setNameOptions(firstNameOptions);
    const nameChips = mapOptionsToChips('firstNameExists', firstNameOptions);
    const photoExistOptions = getOptionsFromBoolFilter(photoOptions, photoExists);
    setPhotoOptions(photoExistOptions);
    const photoChips = mapOptionsToChips('photoExists', photoExistOptions);
    const descriptionExistsOptions = getOptionsFromBoolFilter(descriptionOptions, descriptionExists);
    setonDescriptionOptions(descriptionExistsOptions);
    const descriptionChips = mapOptionsToChips('descriptionExists', descriptionExistsOptions);
    const primaryCountryNames = mergeCountryNames(countryNames, primaryLocations);
    const primaryLocationOptions = getLocationOptions('primary', primaryCountryNames, primaryLocations || []);
    setPrimaryLocationOptions(primaryLocationOptions);
    const primaryLocationChips = mapOptionsToChips('primaryLocations', primaryLocationOptions);
    const secondaryCountryNames = mergeCountryNames(countryNames, secondaryLocations);
    const secondaryLocationOptions = getLocationOptions('secondary', secondaryCountryNames, secondaryLocations || []);
    setSecondaryLocationOptions(secondaryLocationOptions);
    const secondaryLocationChips = mapOptionsToChips('secondaryLocations', secondaryLocationOptions);
    const statusFilterOptions = getCheckedOptions(statusOptions, statuses);
    setStatusFilterOptions(statusFilterOptions);
    const statusFilterChips = mapOptionsToChips('statuses', statusFilterOptions);
    // @ts-ignore
    const mergedChips = [
      ...profileChips,
      ...nameChips,
      ...photoChips,
      ...descriptionChips,
      ...primaryLocationChips,
      ...secondaryLocationChips,
      ...statusFilterChips,
    ];
    setActiveChips(mergedChips);
  }, [filters, countryNames]);

  const onProfileChange = (arr) => {
    const res = getSelectedOptions(arr);
    let filterRes = null;
    if(res.length){
      filterRes = (res[0] === 'true')
    }
    setProfileStatus(filterRes);
    onFilterChange();
  };

  const onNameStatusChange = (arr) => {
    const res = getSelectedOptions(arr);
    let filterRes = null;
    if(res.length){
      filterRes = (res[0] === 'true')
    }
    setFirstNameExists(filterRes);
    onFilterChange();
  };

  const onPhotoStatusChange = (arr) => {
    const res = getSelectedOptions(arr);
    let filterRes = null;
    if(res.length){
      filterRes = (res[0] === 'true');
    }
    setPhotoExists(filterRes);
    onFilterChange();
  };

  const onDescriptionStatusChange = (arr) => {
    const res = getSelectedOptions(arr);
    let filterRes = null;
    if(res.length){
      filterRes = (res[0] === 'true');
    }
    setDescriptionExists(filterRes);
    onFilterChange();
  };

  const onPrimaryLocationChange = (arr) => {
    const res = getSelectedOptions(arr);
    setPrimaryLocations(res);
    onFilterChange();
  }

  const onSecondaryLocationChange = (arr) => {
    const res = getSelectedOptions(arr);
    setSecondaryLocations(res);
    onFilterChange();
  }

  const onStatusChange = (arr) => {
    const res = getSelectedOptions(arr);
    setStatuses(res);
    onFilterChange();
  }

  const onResetFilters = () => {
    onReset();
    onFilterChange();
  };

  const onChipChange = (arr) => {
    const result = arr.reduce((acc, cur) => {
      let currentValue = [cur.value];
      if (cur.value.includes(',')) {
        currentValue = cur.value.split(',');
      }
      acc[cur.type] = [...acc[cur.type], ...currentValue];
      return acc;
    },
    {
      published: [],
      firstNameExists: [],
      photoExists: [],
      descriptionExists: [],
      primaryLocations: [],
      secondaryLocations: [],
      statuses: [],
    });
    const {
      published,
      firstNameExists,
      photoExists,
      descriptionExists,
      primaryLocations,
      secondaryLocations,
      statuses,
    } = result;
    setProfileStatus(published.length ? published[0] === "true" : null);
    setFirstNameExists(firstNameExists.length ? firstNameExists[0] === "true" : null);
    setPhotoExists(photoExists.length ? photoExists[0] === "true" : null);
    setDescriptionExists(descriptionExists.length ? descriptionExists[0] === "true" : null);
    setPrimaryLocations(primaryLocations);
    setSecondaryLocations(secondaryLocations);
    setStatuses(statuses);
    onFilterChange();
  };

  return (
    <>
      <div className={styles['company__filter-wrapper']}>
        <div className={styles.company__filters}>
          <OryxMultiSelect
            key="profile"
            label="Profile Status"
            onChange={onProfileChange}
            options={profileStatus}
            overlayDropdown
            className={styles['company__filter-small']}
            itemClass={styles['company__filter-item']}
          />
          <OryxMultiSelect
            key="name"
            label="First Name"
            onChange={onNameStatusChange}
            options={firstName}
            overlayDropdown
            className={styles['company__filter-small']}
            itemClass={styles['company__filter-item']}
          />
          <OryxMultiSelect
            key="photo"
            label="Photo"
            onChange={onPhotoStatusChange}
            options={photo}
            overlayDropdown
            className={styles['company__filter-small']}
            itemClass={styles['company__filter-item']}
          />
          <OryxMultiSelect
            key="description"
            label="Description"
            onChange={onDescriptionStatusChange}
            options={description}
            overlayDropdown
            className={styles['company__filter-small']}
            itemClass={styles['company__filter-item']}
          />
          <OryxMultiSelect
            key="primaryLocation"
            label="Specialized Countries"
            onChange={onPrimaryLocationChange}
            options={primaryLocation}
            typeahead="name"
            overlayDropdown
            className={styles['company__filter-location']}
            itemClass={styles['company__filter-item']}
          />
          <OryxMultiSelect
            key="secondaryLocatoin"
            label="Secondary Countries"
            onChange={onSecondaryLocationChange}
            options={secondaryLocation}
            typeahead="name"
            overlayDropdown
            className={styles['company__filter-location']}
            itemClass={styles['company__filter-item']}
          />
          <OryxMultiSelect
            key="status"
            label="Status"
            onChange={onStatusChange}
            options={statusFilter}
            overlayDropdown
            className={styles['company__filter-location']}
            itemClass={styles['company__filter-item']}
          />
        </div>
        <OryxButton
          onClick={onResetFilters}
          style={{
            width: '100px',
            flex: '0 1 auto',
            margin: '0 8px',
          }}
        >
          Reset
        </OryxButton>
      </div>
      <div className={styles['company__chips-container']}>
        <ZChips
          data={activeChips}
          onChange={onChipChange}
        />
      </div>
    </>
  );
};

export default CompaniesFiltersView;
